import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { BookingApp, BookingUnavailable, isOnlineBookingOpen } from "./booking/BookingApp";
import { demoSchedulingClient } from "./booking/demoSchedulingClient";
import { demoSchedulingEnabled } from "./booking/publicSchedulingClient";
import { Icon } from "./Icon";

export function PatientEntryApp() {
  return demoSchedulingEnabled() ? <DemoPatientEntryApp /> : <ClinicLinkRequired />;
}

/**
 * A deployed patient app serves many clinics, and this address names none of them, so the page
 * asks for the clinic's own link instead of guessing. It makes no network request.
 */
function ClinicLinkRequired() {
  useEffect(() => {
    document.title = "Bob Doc";
  }, []);
  return (
    <div className="booking-app">
      <header className="booking-topbar">
        <div className="patient-brand">
          <img className="patient-bob-wordmark" src="/brand/bob/wordmark.svg" alt="Bob Doc" />
        </div>
        <span className="secure-label">
          <Icon name="lock" />
          Clinic appointment booking
        </span>
      </header>
      <main className="booking-state-card" aria-labelledby="clinic-link-heading">
        <span className="booking-state-icon">
          <Icon name="location" />
        </span>
        <p className="booking-eyebrow">Bob Doc</p>
        <h1 id="clinic-link-heading">Book with your clinic&apos;s link</h1>
        <p>
          To book online, open the booking link on your clinic&apos;s website or in a message from
          your clinic. If you are at the clinic, please see the front desk.
        </p>
      </main>
    </div>
  );
}

function DemoPatientEntryApp() {
  const configurationQuery = useQuery({
    queryKey: ["booking-configuration", "demo"],
    queryFn: ({ signal }) => demoSchedulingClient.configuration(signal),
  });

  if (configurationQuery.isPending) {
    return (
      <div className="booking-app">
        <main className="booking-state-card booking-loading" role="status">
          <span className="button-spinner" />
          <h1>Opening the clinic page…</h1>
        </main>
      </div>
    );
  }

  if (configurationQuery.isError || configurationQuery.data === undefined) {
    return (
      <div className="booking-app">
        <main className="booking-state-card" aria-labelledby="patient-entry-error-heading">
          <p className="booking-eyebrow">Connection problem</p>
          <h1 id="patient-entry-error-heading">This page is temporarily unavailable.</h1>
          <p>
            The scheduling service could not be reached. Please see the front desk, or try again. No
            information was submitted.
          </p>
          <button
            type="button"
            className="continue-button"
            onClick={() => void configurationQuery.refetch()}
          >
            Try again
          </button>
        </main>
      </div>
    );
  }

  // BookingApp shares this configuration query and moves the address to the booking path.
  return isOnlineBookingOpen(configurationQuery.data) ? (
    <BookingApp />
  ) : (
    <BookingUnavailable configuration={configurationQuery.data} embedded={false} />
  );
}
